import React, { useReducer, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  DEFAULT_FINANCE_OPTION,
  DEFAULT_FINANCE_TERM,
  MC_PRODUCT_LINE_NAME,
  TOTAL_DROPDOWN_LEVEL,
} from '../../utils/constants';

const getInitialOptionsState = length => Array(length).fill(null);
const initialOptions = getInitialOptionsState(TOTAL_DROPDOWN_LEVEL);

const initialState = {
  currentProductLine: MC_PRODUCT_LINE_NAME,
  finance: {
    options: DEFAULT_FINANCE_OPTION,
    term: DEFAULT_FINANCE_TERM,
    downPayment: 0,
    includesSalesTax: false,
  },
  selectedOptions: initialOptions,
  accessories: [],
  warranty: '',
  exteriorColor: '',
};

export const SelectProductsContext = createContext(initialState);
SelectProductsContext.displayName = 'SelectProductsContext';

const SelectProductsDispatchContext = createContext(() => {});
SelectProductsDispatchContext.displayName = 'SelectProductsDispatchContext';

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_PRODUCT_LINE':
      return { ...state, currentProductLine: action.payload };

    case 'SET_FINANCE':
      return { ...state, finance: { ...state.finance, ...action.payload } };

    case 'SET_SELECTED_OPTIONS': {
      const prev = state.selectedOptions;
      const { targetNode, index } = action.payload || {};

      if (targetNode && typeof index === 'number') {
        const update = [...prev.slice(0, index), targetNode, ...prev.slice(index + 1).fill(null)];
        return { ...state, selectedOptions: update };
      }
      // when payload data is invalid return previous state
      return state;
    }

    case 'RESET_SELECTED_OPTIONS':
      return { ...state, selectedOptions: initialOptions };

    case 'REPLACE_SELECTED_OPTIONS':
      return { ...state, selectedOptions: action.payload };

    case 'SET_ACCESSORIES':
      return { ...state, accessories: action.payload };

    case 'ADD_ACCESSORIES': {
      const prev = state.accessories || [];
      return { ...state, accessories: [...prev, action.payload] };
    }

    case 'REMOVE_ACCESSORIES': {
      const prev = state.accessories || [];
      const removedAccessory = action.payload;
      const update = prev.filter(accessory => accessory !== removedAccessory);
      return { ...state, accessories: update };
    }

    case 'SET_WARRANTY':
      return { ...state, warranty: action.payload };

    case 'SET_EXTERIOR_COLOR':
      return { ...state, exteriorColor: action.payload };

    default:
      return state;
  }
};

// hook for exposing currentProductLine
export const useCurrentProductLine = () => {
  const { currentProductLine } = useContext(SelectProductsContext);
  const dispatch = useContext(SelectProductsDispatchContext);
  const setCurrentProductLine = productLine => dispatch({ type: 'SET_PRODUCT_LINE', payload: productLine });

  return [currentProductLine, setCurrentProductLine];
};

export const useSelectedOptions = () => {
  const { selectedOptions } = useContext(SelectProductsContext);
  const dispatch = useContext(SelectProductsDispatchContext);

  /**
   * update individual option node
   *
   * @param {Object} payload
   * @param {Object} payload.targetNode - New node to insert into options array
   * @param {number} payload.index - Index of current level
   */
  const setSelectedOptions = payload => dispatch({ type: 'SET_SELECTED_OPTIONS', payload });

  /**
   * replace entire options
   *
   * @param {Object[]} payload - An array of options nodes
   */
  const replaceSelectedOptions = payload => dispatch({ type: 'REPLACE_SELECTED_OPTIONS', payload });
  /**
   * reset options to initial state
   *
   */
  const resetSelectedOptions = () => dispatch({ type: 'RESET_SELECTED_OPTIONS' });

  return { selectedOptions, setSelectedOptions, replaceSelectedOptions, resetSelectedOptions };
};

export const useFinance = () => {
  const { finance } = useContext(SelectProductsContext);
  const dispatch = useContext(SelectProductsDispatchContext);
  const setFinance = update => dispatch({ type: 'SET_FINANCE', payload: update });

  return [finance, setFinance];
};

export const useAccessories = () => {
  const { accessories } = useContext(SelectProductsContext);
  const dispatch = useContext(SelectProductsDispatchContext);

  const setAccessory = update => {
    dispatch({ type: 'SET_ACCESSORIES', payload: update });
  };
  const addAccessory = addedAccessory => {
    dispatch({ type: 'ADD_ACCESSORIES', payload: addedAccessory });
  };
  const removeAccessory = removedAccessory => {
    dispatch({ type: 'REMOVE_ACCESSORIES', payload: removedAccessory });
  };
  return { accessories, setAccessory, addAccessory, removeAccessory };
};

export const useWarranty = () => {
  const { warranty } = useContext(SelectProductsContext);
  const dispatch = useContext(SelectProductsDispatchContext);
  const setWarranty = update => dispatch({ type: 'SET_WARRANTY', payload: update });

  return [warranty, setWarranty];
};

export const useExteriorColor = () => {
  const { exteriorColor } = useContext(SelectProductsContext);
  const dispatch = useContext(SelectProductsDispatchContext);
  const setExteriorColor = update => dispatch({ type: 'SET_EXTERIOR_COLOR', payload: update });

  return [exteriorColor, setExteriorColor];
};

const SelectProductsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <SelectProductsContext.Provider value={state}>
      <SelectProductsDispatchContext.Provider value={dispatch}>{children}</SelectProductsDispatchContext.Provider>
    </SelectProductsContext.Provider>
  );
};

SelectProductsProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
export default SelectProductsProvider;
